import { ChangeEvent, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { getSystemSettingHistory } from "../../../services";
import {
  SystemSetting,
  SystemSettingHistoryRow,
} from "../../../types/system-setting.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { HISTORY_PAGE_SIZE, settingLabel } from "./settingMeta";
import { formatSettingValue } from "./settingValue";

const COLUMNS = ["เวลา", "ค่าเดิม", "ค่าใหม่", "แก้ไขโดย"];
const SKELETON_ROWS = Array.from({ length: HISTORY_PAGE_SIZE }, (_, index) => index);
const FIRST_PAGE = 1;
const TITLE_SX = { fontWeight: 700, pr: 6 } as const;
const CLOSE_BUTTON_SX = { position: "absolute", right: 8, top: 8 } as const;
const NO_WRAP_SX = { whiteSpace: "nowrap" } as const;
const EMPTY_CELL_SX = { py: 5 } as const;
const ACTIONS_SX = { p: 2 } as const;

interface SettingHistoryDialogProps {
  setting: SystemSetting;
  onClose: () => void;
}

/** Every saved change of one key, newest first, a page at a time. */
export default function SettingHistoryDialog({
  setting,
  onClose,
}: Readonly<SettingHistoryDialogProps>) {
  const [page, setPage] = useState<number>(FIRST_PAGE);
  const [rows, setRows] = useState<SystemSettingHistoryRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isLatest = true;
    setLoading(true);
    getSystemSettingHistory(setting.key, page, HISTORY_PAGE_SIZE)
      .then((response) => {
        if (!isLatest) return;
        setRows(response.data.data?.rows ?? []);
        setCount(response.data.data?.count ?? 0);
      })
      .catch(() => {
        // The axios interceptor has shown the server's message; the table empties rather
        // than keep a page that no longer answers the one asked for.
        if (!isLatest) return;
        setRows([]);
        setCount(0);
      })
      .finally(() => {
        if (isLatest) setLoading(false);
      });
    return () => {
      isLatest = false;
    };
  }, [setting.key, page]);

  const pageCount = Math.ceil(count / HISTORY_PAGE_SIZE);
  const isEmpty = !loading && rows.length === 0;

  const handleChangePage = (_event: ChangeEvent<unknown>, nextPage: number) =>
    setPage(nextPage);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={TITLE_SX}>
        ประวัติการแก้ไข · {settingLabel(setting.key)}
        <IconButton aria-label="close" onClick={onClose} sx={CLOSE_BUTTON_SX}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                SKELETON_ROWS.map((row) => (
                  <TableRow key={row}>
                    {COLUMNS.map((column) => (
                      <TableCell key={column}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {isEmpty && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={EMPTY_CELL_SX}>
                    <Typography color="text.secondary">ยังไม่มีประวัติการแก้ไข</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={NO_WRAP_SX}>
                      {formatThaiShortDateTimeBkk(row.changed_at, { weekday: false })}
                    </TableCell>
                    <TableCell>{formatSettingValue(row.old_value, setting.key)}</TableCell>
                    <TableCell>{formatSettingValue(row.new_value, setting.key)}</TableCell>
                    <TableCell>{row.changed_by}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pageCount > 1 && (
          <Stack alignItems="center" mt={2}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button variant="outlined" onClick={onClose}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
